import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../context/ToastContext";
import { getPageData } from "../util/Utils";
import { DASHBOARD_REFRESH_MS } from "../util/GlobalConstants";
import { useCurrentDevice } from "../context/CurrentDeviceContext";
import { useCurrentId } from "../context/CurrentIdContext";
import {
  getWorkstations,
  deleteDevice,
  getLinuxList,
  getMobileList,
  getServerList,
  getWindowsList,
  getAppleList,
} from "../Api";
import { Table } from "rsuite";
import offImg from "../resources/off.png";
import onImg from "../resources/on.png";
import androidImg from "../resources/android_logo.png";
import linuxImg from "../resources/linux_logo.png";
import windowsImg from "../resources/windows_logo.png";
import AppleImg from "../resources/apple.png";
import DeviceMenu from "../components/DeviceMenu";
import MessageBox from "./MessageBox";
import AlarmsMenu from "../components/AlarmsMenu";
import DeviceBatteryStatus from "./DeviceBatteryStatus";
import CustomImage from "./CustomImage";
import Spinner, { spinnerTypes } from "./Spinner";
import dev from "../models/joinDevice.json";
const { Column, HeaderCell, Cell } = Table;

export const filterType = {
  all: "all-devices",
  windows: "windows-devices",
  linux: "linux-devices",
  mobile: "mobile-devices",
  server: "servers-devices",
  apple: "apple-devices",
};
export const menuType = {
  options: "devices-options",
  alarms: "device-alarms",
};

const DevicesTable = ({
  filteredBy = filterType.all,
  setItems,
  selectedDevice,
  selectDevice,
  activeDataUpdate,
  setActiveDataUpdate,
  columnsShown,
  setDefaultColumns,
  page = 1,
  limit = 0,
  filteredItems = [],
  className,
  contextMenuType = menuType.options,
}) => {
  const [t] = useTranslation();
  const [devices, setDevices] = useState([]);
  const [currentDevice, setCurrentDevice] = useState();
  const [showMenu, setShowMenu] = useState(false);
  const [xYPosition, setXYPosition] = useState({ x: 0, y: 0 });
  const { currentDevice: deviceSelected, setCurrentDevice: setDeviceSelected } =
    useCurrentDevice();
  const { setCurrentId } = useCurrentId();
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(t("_common.loading"));
  const toast = useToast();
  const timeRequest = DASHBOARD_REFRESH_MS;
  const pendingRequest = useRef(false);
  const filter = useRef();
  const interval = useRef(null);
  const copyColumns = useRef([]);
  const sorting = (items) => {
    return items.sort((a, b) => {
      let x = a[sortColumn];
      let y = b[sortColumn];
      let isString = false;
      if (sortColumn === dev.timeon) {
        let h1 = x.split(":");
        let h2 = y.split(":");
        let t1 = new Date();
        let t2 = new Date();
        t1.setHours(h1[0], h1[1], h1[2]);
        t2.setHours(h2[0], h2[1], h2[2]);
        x = t1;
        y = t2;
      } else {
        if (typeof x === "string" && typeof y === "string") {
          x = x.toLocaleLowerCase();
          y = y.toLocaleLowerCase();
          isString = true;
        }
      }

      if (sortType === "asc") {
        if (isString) return x.localeCompare(y);
        else return x - y;
      } else {
        if (isString) return y.localeCompare(x);
        else return y - x;
      }
    });
  };
  const getData = () => {
    if (filteredItems.length > 0) {
      const devicesFiltered = devices.filter((v) => {
        return filteredItems.find((e) => e.value === v.id) !== undefined;
      });
      if (sortColumn && sortType) {
        return sorting(devicesFiltered);
      }
      return devicesFiltered;
    } else {
      if (sortColumn && sortType) {
        return sorting(devices);
      }
      return devices;
    }
  };
  const data = getPageData(getData(), page, limit);

  useEffect(() => {
    const defaultColumns = [
      {
        key: dev.id,
        label: t("devices-table.id"),
        width: 140,
        sortable: true,
        fullText: true,
      },
      {
        key: dev.sysname,
        label: t("devices-table.npc"),
        width: 130,
        sortable: true,
        fullText: true,
      },
      {
        key: dev.state,
        label: t("devices-table.state"),
        width: 80,
        sortable: true,
      },
      {
        key: dev.type,
        label: t("devices-table.type"),
        width: 80,
        sortable: true,
      },
      {
        key: dev.usrname,
        label: t("devices-table.user"),
        width: 150,
        sortable: true,
        fullText: true,
      },
      {
        key: dev.os,
        label: t("devices-table.os"),
        width: 220,
        sortable: true,
        fullText: true,
      },
      {
        key: dev.ip,
        label: t("devices-table.ip"),
        width: 120,
      },
      {
        key: dev.timeon,
        label: t("devices-table.last-response"),
        width: 160,
        sortable: true,
      },
      {
        key: dev.ver,
        label: t("devices-table.version"),
        width: 80,
      },
      {
        key: "battery",
        label: t("devices-table.battery"),
        width: 80,
      },
    ];
    if (filteredBy === filterType.mobile) {
      defaultColumns.push({
        key: "carrier",
        label: t("devices-table.carrier"),
        width: 100,
      });
    }
    copyColumns.current = defaultColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBy, t]);

  function updateCellSizes(data) {
    if (data.length > 0) {
      var maxSizes = Array(copyColumns.current.length).fill(0);
      data.forEach((element) => {
        for (let i = 0; i < maxSizes.length; i++) {
          let key = copyColumns.current[i].key;
          let value = element[key];
          maxSizes[i] = Math.max(maxSizes[i], value.length);
        }
      });
      copyColumns.current.forEach((item, index) => {
        if (item.key === "battery") maxSizes[index] = maxSizes[index] + 2;
        if (item.key === dev.state || item.key === dev.type)
          maxSizes[index] = 7;
        if (item.key === dev.ip) maxSizes[index] = maxSizes[index] - 2;
        if (item.key === dev.timeon) maxSizes[index] = maxSizes[index] - 1;
        item.width = Math.round(maxSizes[index] * 8.2 + 20);
      });
    }
    setDefaultColumns(copyColumns.current);
  }

  function requestWorkstations() {
    getWorkstations().then((data) => {
      if (filter.current === filterType.all) {
        if (data != null) {
          updateCellSizes(data);
          setDevices(data);
        } else setDevices([]);
        setLoading(false);
      }
      pendingRequest.current = false;
    });
  }

  function requestLinuxList() {
    getLinuxList().then((data) => {
      if (filter.current === filterType.linux) {
        if (data != null) {
          updateCellSizes(data);
          setDevices(data);
        } else setDevices([]);
        setLoading(false);
      }
      pendingRequest.current = false;
    });
  }

  function requestMobileList() {
    getMobileList().then((data) => {
      if (filter.current === filterType.mobile) {
        if (data != null) {
          updateCellSizes(data);
          setDevices(data);
        } else setDevices([]);
        setLoading(false);
      }
      pendingRequest.current = false;
    });
  }

  function requestServerList() {
    getServerList().then((data) => {
      if (filter.current === filterType.server) {
        if (data != null) {
          updateCellSizes(data);
          setDevices(data);
        } else setDevices([]);
        setLoading(false);
      }
      pendingRequest.current = false;
    });
  }

  function requestAppleList() {
    getAppleList().then((data) => {
      if (filter.current === filterType.apple) {
        if (data != null) {
          updateCellSizes(data);
          setDevices(data);
        } else setDevices([]);
        setLoading(false);
      }
      pendingRequest.current = false;
    });
  }

  function requestWindowsList() {
    getWindowsList().then((data) => {
      if (filter.current === filterType.windows) {
        if (data != null) {
          updateCellSizes(data);
          setDevices(data);
        } else setDevices([]);
        setLoading(false);
      }
      pendingRequest.current = false;
    });
  }

  function searchFilter() {
    pendingRequest.current = true;
    switch (filter.current) {
      case filterType.linux:
        requestLinuxList();
        break;
      case filterType.mobile:
        requestMobileList();
        break;
      case filterType.server:
        requestServerList();
        break;
      case filterType.windows:
        requestWindowsList();
        break;
      case filterType.apple:
        requestAppleList();
        break;
      default:
        requestWorkstations();
    }
  }

  useEffect(() => {
    if (typeof filteredBy === "string") {
      filter.current = filteredBy;
      if (typeof interval.current === "number") {
        clearInterval(interval.current);
        interval.current = null;
      }
      setDefaultColumns([]);
      let message = "";
      switch (filteredBy) {
        case filterType.linux:
          message = t("devices-table.loading-linux-dashboard");
          break;
        case filterType.mobile:
          message = t("devices-table.loading-android-dashboard");
          break;
        case filterType.server:
          message = t("devices-table.loading-servers-dashboard");
          break;
        case filterType.windows:
          message = t("devices-table.loading-windows-dashboard");
          break;
        case filterType.apple:
          message = t("devices-table.loading-apple-dashboard");
          break;
        default:
          message = t("_common.loading");
      }
      setLoadingMessage(message);
      setLoading(true);
      setDevices([]);
      interval.current = setInterval(() => {
        if (!pendingRequest.current) searchFilter();
      }, timeRequest);

      return () => {
        clearInterval(interval.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBy]);

  useEffect(() => {
    if (devices) setItems(devices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  useEffect(() => {
    if (selectedDevice) {
      setCurrentDevice(selectedDevice);
    }
  }, [selectedDevice]);

  useEffect(() => {
    if (typeof activeDataUpdate === "boolean" && activeDataUpdate) {
      setLoadingMessage(t("_common.loading"));
      setLoading(true);
      searchFilter();
      setActiveDataUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDataUpdate]);

  const deleteDeviceSelected = () => {
    setLoadingMessage(t("devices-table.deleting-item"));
    setLoading(true);
    deleteDevice(deviceSelected.id).then((response) => {
      if (response) {
        toast.success(
          t("devices-table.item-deleted"),
          t("devices-table.deletion-notification")
        );
      } else {
        toast.error(
          t("devices-table.error-request"),
          t("devices-table.deletion-notification")
        );
      }

      setLoading(false);
    });
  };

  const showDevicemenu = (event, currentDev) => {
    event.preventDefault();
    setShowMenu(false);
    const positionChange = {
      x: event.pageX,
      y: event.pageY,
    };
    setXYPosition(positionChange);
    setDeviceSelected(currentDev);
    setCurrentId(currentDev.id);
    setShowMenu(true);
  };

  const hideContext = () => {
    setShowMenu(false);
  };

  const handleSortColumn = (sortColumn, sortType) => {
    setLoadingMessage(t("devices-table.sorting-columns"));
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSortColumn(sortColumn);
      setSortType(sortType);
    }, 500);
  };

  const EmptyTable = () => <MessageBox message={t("_common.empty-message")} />;
  const LoadingTable = () => (
    <Spinner
      message={loadingMessage}
      spinnerType={spinnerTypes.RotatingSquare}
    />
  );
  const CustomCell = ({ rowData, dataKey, rowIndex, ...props }) =>
    dataKey === dev.state ? (
      <Cell
        {...props}
        style={{ padding: 0 }}
        className={
          rowData.id === currentDevice
            ? "cell-active"
            : rowIndex % 2 === 0
            ? "cell-bg-even"
            : null
        }
        onContextMenu={(e) => showDevicemenu(e, rowData)}
      >
        <div className="img-div">
          <CustomImage
            src={rowData.state === dev.typestates.on ? onImg : offImg}
            className="img-table"
            alt={rowData.state}
          />
        </div>
      </Cell>
    ) : dataKey === dev.type ? (
      <Cell
        {...props}
        style={{ padding: 0 }}
        className={
          rowData.id === currentDevice
            ? "cell-active"
            : rowIndex % 2 === 0
            ? "cell-bg-even"
            : null
        }
        onContextMenu={(e) => showDevicemenu(e, rowData)}
      >
        <div className="img-div">
          <CustomImage
            src={
              rowData[dataKey] === dev.types.android
                ? androidImg
                : rowData[dataKey] === dev.types.windows
                ? windowsImg
                : rowData[dataKey] === dev.types.apple
                ? AppleImg
                : linuxImg
            }
            className="img-table"
            alt={rowData[dataKey]}
          />
        </div>
      </Cell>
    ) : dataKey === "battery" ? (
      <Cell
        {...props}
        className={
          rowData.id === currentDevice
            ? "cell-active"
            : rowIndex % 2 === 0
            ? "cell-bg-even"
            : null
        }
        onContextMenu={(e) => showDevicemenu(e, rowData)}
      >
        <DeviceBatteryStatus batteryString={rowData[dataKey]} />
      </Cell>
    ) : (
      <Cell
        {...props}
        className={
          rowData.id === currentDevice
            ? "cell-active"
            : rowIndex % 2 === 0
            ? "cell-bg-even"
            : null
        }
        onContextMenu={(e) => showDevicemenu(e, rowData)}
      >
        {rowData[dataKey]}
      </Cell>
    );

  function onRowClickHandler(rowData) {
    if (showMenu) hideContext();
    else {
      setCurrentDevice(rowData.id);
      selectDevice(rowData.id);
    }
  }

  return (
    <>
      <Table
        height={300}
        rowHeight={32}
        data={data}
        onRowClick={(rowData) => onRowClickHandler(rowData)}
        bordered={true}
        cellBordered={true}
        loading={loading}
        renderLoading={LoadingTable}
        renderEmpty={EmptyTable}
        rowKey={dev.id}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSortColumn}
        className={className}
      >
        {columnsShown.map((column) => {
          const { key, label, ...rest } = column;
          return (
            <Column {...rest} key={key} resizable>
              <HeaderCell>{label}</HeaderCell>
              <CustomCell dataKey={key} />
            </Column>
          );
        })}
      </Table>
      {contextMenuType === menuType.options && (
        <DeviceMenu
          x={xYPosition.x}
          y={xYPosition.y}
          deleteDeviceSelected={deleteDeviceSelected}
        />
      )}
      {contextMenuType === menuType.alarms && (
        <AlarmsMenu x={xYPosition.x} y={xYPosition.y} />
      )}
    </>
  );
};

export default DevicesTable;
